/**
 * This file exists for one reason:
 * When a player folds, we need to keep their bet displayed near their seat
 * until the end of the round. Only then do the chips animate into the pot
 *
 * By default, chips move to the pot immediately on fold,
 * because the server sets the player's bet to 0 right away
 *
 * Default (simplified) logic (used when no chip delay is needed):
 *
 *   const playerStacks = computed(() =>
 *     roomState.value.seats.map(
 *       (seat) => (seat && roomState.value.hand?.bets[seat.playerId]) ?? 0,
 *     ),
 *   );
 */

import { computed, ref, watch } from 'vue'
import { isEqual } from 'lodash'
import { Round } from '@/constants'
import type { Ref } from 'vue'
import type { Bets, RoomState } from '@acepoker/contract/acepoker'
import type { HandHistoryAction } from '@acepoker/poker-core'

type BetValue = Bets[keyof Bets]

interface UsePlayerStacks {
  (roomState: Ref<RoomState>): {
    playerStacks: BetValue[]
  }
}

export const usePlayerStacks: UsePlayerStacks = (roomState) => {
  const bets = ref<Bets | null>(null)

  const activeAction = computed(() => {
    const { handHistory } = roomState.value

    if (handHistory?.length) {
      return handHistory[handHistory.length - 1]
    }

    return null
  })
  const stacks = computed((): BetValue[] => {
    if (!bets.value) {
      return []
    }
    return roomState.value.seats.map((seat) => {
      if (!seat || !bets.value) {
        return 0
      }
      return bets.value[seat.playerId] ?? 0
    })
  })

  const resetBets = () => {
    if (!bets.value) {
      return
    }
    for (const key in bets.value) {
      bets.value[key] = 0
    }
  }
  const isCommunityCardAction = (action: HandHistoryAction) => {
    return [Round.FLOP, Round.TURN, Round.RIVER].includes(action.type)
  }

  watch(
    () => roomState.value?.hand?.bets,
    (newBets) => {
      if (newBets && !bets.value) {
        bets.value = newBets
      }

      if (!activeAction.value) {
        bets.value = newBets ?? null
        return
      }

      if (isCommunityCardAction(activeAction.value)) {
        resetBets()
        return
      }

      if (!newBets || !bets.value) {
        return
      }

      for (const key in newBets) {
        // This ensures we keep the bet value even after
        // the server has reset bet for folded players to 0
        if (newBets[key] > 0) {
          bets.value[key] = newBets[key]
        }
      }
    }
  )
  watch(
    () => roomState.value?.hand?.pots,
    (newPots, prevPots) => {
      const isPotsChanged = newPots?.length && prevPots?.length && !isEqual(newPots, prevPots)

      if (isPotsChanged) {
        resetBets()
      }
    }
  )

  return {
    playerStacks: stacks
  }
}
